import API_URLS from "@/constants/apiUrls";
import { HTTP_METHODS } from "@/constants/httpMethods";
import { request } from "@/lib/api";

/**
 * Hotel Detail Service
 * Otel detay sayfası API'leri için servis
 */
export const hotelDetailService = {
  /**
   * Otel detay veya landing sayfası için veri getir
   * @param {Object} params - Parametreler
   * @param {string} params.destination - Otel slug'ı (örn: "grand-itimat-hotel")
   * @param {boolean} params.isLandingPage - Landing sayfası mı? (query varsa false, yoksa true)
   * @param {string} [params.checkinDate] - Giriş tarihi (format: "YYYY.MM.DD") - sadece detay sayfasında
   * @param {string} [params.checkoutDate] - Çıkış tarihi (format: "YYYY.MM.DD") - sadece detay sayfasında
   * @param {Array} [params.occupancies] - Misafir bilgileri - sadece detay sayfasında
   * @param {Object} [params.preferences] - Tercihler
   * @param {string} [params.preferences.language] - Dil kodu (default: "tr")
   * @param {string} [params.preferences.currency] - Para birimi (default: "TRY")
   * @param {string} [params.preferences.nationality] - Uyruk (default: "TR")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   */
  getHotelDetailOrLanding: async (
    {
      destination,
      isLandingPage = false,
      checkinDate = null,
      checkoutDate = null,
      campaignId = 0,
      occupancies = [],
      preferences = {},
    } = {},
    headers = {},
    hasErrorHandler = false,
  ) => {
    // Preferences için default değerler
    const defaultPreferences = {
      Language: "tr",
      Currency: "TRY",
      Nationality: "TR",
      DeviceType: null,
    };

    // Preferences mapping: camelCase -> PascalCase
    const mappedPreferences = {
      Language: preferences.language || defaultPreferences.Language,
      Currency: preferences.currency || defaultPreferences.Currency,
      Nationality: preferences.nationality || defaultPreferences.Nationality,
      DeviceType: preferences.deviceType ?? defaultPreferences.DeviceType,
    };

    // SearchFilters oluştur
    const searchFilter = {
      Destination: destination,
      CampaignId: (() => {
        const parsed = parseInt(String(campaignId ?? "").trim(), 10);
        return Number.isFinite(parsed) ? parsed : 0;
      })(),
    };

    // Eğer detay sayfası ise (isLandingPage = false), tarih ve misafir bilgilerini ekle
    if (!isLandingPage && checkinDate && checkoutDate) {
      searchFilter.CheckinDate = checkinDate;
      searchFilter.CheckoutDate = checkoutDate;
      
      if (occupancies && occupancies.length > 0) {
        searchFilter.Occupancies = occupancies.map((occupancy) => ({
          PaxList: occupancy.paxList.map((pax) => ({
            Type: pax.type,
            Count: pax.count,
            Age: pax.age,
          })),
        }));
      }
    }

    // Payload oluştur
    const payload = {
      SearchForm: {
        ScrollIndex: 1,
        IsScroll: false,
        IsLandingPage: isLandingPage,
        SearchFilters: [searchFilter],
        Preferences: mappedPreferences,
      },
    };

    try {

      const response = await request({
        method: HTTP_METHODS.POST,
        url: API_URLS.hotel.detail.getHotelDetailOrLanding,
        payload,
        headers,
        hasErrorHandler,
      });

      return response;
    } catch (error) {
      console.error('❌ API Request Error:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        error: error,
      });
      throw error;
    }
  },

  /**
   * Otel detay sayfası "Hakkında Daha Fazlası" için diğer ürün linkleri (uçak, otobüs, araç kiralama vb.)
   * @param {Object} params
   * @param {string} params.Slug - Otel/lokasyon slug (örn: "akra-antalya")
   * @param {string} [params.LanguageCode="tr"] - Dil kodu
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi
   * @returns {Promise<{ OtherProducts: Array<{ Name: string, Slug: string, RedirectUrl: string }> }>}
   */
  getOtherProducts: (
    { Slug, LanguageCode = "tr" } = {},
    headers = {},
    hasErrorHandler = false,
  ) => {
    const payload = {
      Slug,
      LanguageCode: (LanguageCode || "tr").toLowerCase(),
    };
    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.detail.getOtherProducts,
      payload,
      headers,
      hasErrorHandler,
    });
  },
};

